<template>
  <Loader :value="promises.fetchOne" :class="['task-views-default', `task-views-default--type-${item.type}`]">
    <div class="task-views-default__content">
      <Card v-bind="$attrs" class="task-views-default__card" theme="flat">
        <template #header>
          <component
            v-if="item.reference"
            :is="ReferenceComponents[item.reference.onModel]"
            :value="item.reference.ref"
            class="task-views-default__reference"
          />
        </template>
        <template #default>
          <div class="row between g-2 top mb-2">
            <div class="card__title">{{ item.title }}</div>
            <div class="task-views-default__actions row g-1">
              <ButtonGroup v-if="$auth.isAuthenticated" :use-portal="false">
                <ButtonComponent :label="$t('edit')" :icon="['fal', 'pen']" @click="$emit('mutate')" />
                <template #secondary>
                  <ButtonComponent
                    :label="$t('delete')"
                    :promise="promises.remove"
                    :icon="['fal', 'trash']"
                    confirm
                    @click="() => remove({ id })"
                    @onSuccess="onRemove"
                  />
                </template>
              </ButtonGroup>

              <Actions placement="task" :query="{ task: id }" />
            </div>
          </div>
          <Metadata :item="item">
            <template #status>
              <Status
                :value="item.status"
                @input="setStatus"
                :promise="innerPromises.status"
                :readonly="isSlim || Boolean($path('metadata.disabledFields.status', item)) || !$auth.isAuthenticated"
                v-tooltip="$path('metadata.disabledFields.status', item)"
              /> </template
          ></Metadata>

          <div class="task-views-default__description" v-html="description"></div>
          <FileComponent
            v-if="item.attachments && item.attachments.length"
            :label="$tc('attachment', 2)"
            :value="item.attachments"
            readonly
          />
          <Thumbnails v-if="item.images && item.images.length" :label="$tc('image', 2)" :value="item.images" />
        </template>
        <template #footer>
          <ButtonComponent
            v-if="mode !== 'reject'"
            style="margin-right: auto"
            :icon="['fal', 'times']"
            :label="$t('close')"
            @click="$emit('close')"
          />
          <!-- <slot name="actions" /> -->

          <Request
            v-if="$auth.isAuthenticated && item.status === 'request' && mode !== 'reject'"
            :promise="innerPromises.status"
            @input="setStatus"
          />
          <Reject v-if="mode === 'reject'" :promise="innerPromises.status" @input="setStatus" @cancel="mode = null" />
          <p v-if="error && isSlim">Error: {{ error }}</p>
        </template>
      </Card>

      <Comments
        v-if="!isSlim"
        :reference="id"
        scope="Task"
        :value="item.comments"
        :promise="innerPromises.comment"
        @submit="comment"
        class="task-views-default__content-comments"
      >
        <template #after-input>
          <Alert class="task-views-default__comment-alert">{{ $t('taskCommentsReceiverInfo') }}</Alert>
        </template>
      </Comments>
    </div>
  </Loader>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'

import { DropdownComponent } from 'vue-elder-dropdown'
import { FileComponent } from '@kvass/file'
import Card from '@/components/Card'
import UserCard from '@/components/User/Card'
import ReferenceComponents from '@/components/Task/Reference'
import Thumbnails from '@/components/Thumbnails/Component'
import Status from '@/components/Task/Status/Default'
import Request from '@/components/Task/Status/Request'
import Metadata from '@/components/Task/Metadata'

import Reject from '@/components/Task/Status/Reject'
import { AlertComponent as Alert } from 'vue-elder-alert'
import CommentApi from '@/api/Comment'
import Comments from '@/components/Comment/List'
import Actions from '@/components/Actions'
import ButtonGroup from '@kvass/button-group'

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  watch: {
    id: {
      handler(id) {
        if (this.item && this.item.id !== id) this.set({})
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  data() {
    return {
      innerPromises: {
        status: null,
        comment: null,
      },
      error: null,
      mode: null,
      ReferenceComponents,
    }
  },
  computed: {
    ...mapState('Task', ['item', 'promises', 'states']),
    ...mapi18n('labels', {
      title: 'title',
      members: 'member',
      images: ['image', 2],
      attachments: ['attachment', 2],
      description: 'description',
    }),
    isSlim() {
      return this.item.type === 'slim'
    },

    description() {
      return (
        this.item.description ||
        `<span class="task-views-default__description-placeholder">${this.$t('noDescription')}</span>`
      )
    },
  },
  methods: {
    ...mapActions('Task', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Task', ['set', 'addComment']),
    setStatus(status, comment) {
      if (status === 'reject') {
        switch (this.item.type) {
          case 'slim':
            status = 'rejected'
            break
          default:
            return (this.mode = status)
        }
      }

      let data = { id: this.id, status }
      this.innerPromises.status = this.mutate(data)
        .then(() => {
          this.$bus.$emit('task:update', {
            ...data,
            reference: this.item.reference,
          })

          if (status === 'rejected' && comment) {
            this.comment({ message: comment })
            this.mode = null
          }

          this.$bus.$emit('actions:refetch', 'task')

          this.fetch({ id: this.id }) // Todo: implement disabledFields system to prevent refetch
        })
        .catch(e => {
          const error =
            e.response && e.response.errors && Array.isArray(e.response.errors) && e.response.errors.length > 0
              ? e.response.errors.map(err => err.message).join(', ')
              : typeof e === 'object' && 'message' in e
              ? e.message
              : e
          this.error = error
        })
    },
    comment(data) {
      return (this.innerPromises.comment = CommentApi.mutate({
        reference: { onModel: 'Task', ref: this.id },
        ...data,
      }).then(data => this.addComment(data)))
    },
    onRemove() {
      this.$emit('onRemove')
      this.$bus.$emit('task:remove', this.id)
    },
  },
  components: {
    Card,
    FileComponent,
    UserCard,
    Thumbnails,
    Comments,
    DropdownComponent,
    Status,
    Request,
    Reject,
    Actions,
    Alert,
    ButtonGroup,
    Metadata,
  },
}
</script>

<style lang="scss">
.task-views-default {
  max-width: 1300px;

  &--type-slim {
    max-width: 600px;
  }

  &:not(&--type-slim) .task-views-default__content > .card {
    &:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    & + .card {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    justify-content: center;

    &-comments {
      border-left: 1px solid var(--border-color);

      .comments__list {
        max-height: 650px;
      }

      @include respond-below('phone') {
        .comments__list {
          max-width: 300px;

          margin: 0 auto;
          overflow-x: auto;
        }
      }
    }
  }

  &__actions {
    font-size: 0.8em !important;

    .elder-dropdown__content {
      font-size: 1em;
    }
  }

  &__reference {
    display: flex;
  }

  &__description {
    border-left: 2px solid var(--border-color);
    padding: 1rem;
    background-color: css-lighten('border-color', 9%);

    & > * {
      margin: 0;
    }
  }

  .card__title {
    margin: 0;
    font-size: 1.5em;
    align-self: center;
  }

  &__comment-alert {
    border-top: 1px solid var(--border-color);
    border-radius: calc(var(--border-radius-md) - 1px);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    overflow: hidden;

    .alert__content {
      padding: 0.5rem 1rem;
    }
  }

  &__card {
    & > .card__header {
      min-height: 75px;
    }

    & > .card__content {
      position: relative;
      display: flex;
      flex-direction: column;

      grid-gap: 1rem 2rem;

      & > *:last-child {
        margin-bottom: auto;
      }
    }

    & > .card__footer {
      .card__footer-content {
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
